import { TextField, Select, MenuItem } from "@material-ui/core";
import { translate } from "../../translations/index.ts";
import { USER_ROLES } from "../../utils";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import APIclient from "../../api-client/index.js";

const EMAIL_REGEXP = new RegExp(/^[^@\s]+@[^@\s]+$/);
const TARGET_AUDIENCES =
  import.meta.env.VITE_TARGET_AUDIENCES?.split(",") || [];

export default function Signup() {
  const { token } = useParams();
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const [newUser, setNewUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    organisation: "",
    organisationLocation: "",
    targetAudience: "",
    password: "",
    confirmPassword: "",
  });

  const addError = (newErrors) => {
    setErrors((current) => ({ ...current, ...newErrors }));
  };

  const register = async (event) => {
    event.preventDefault();
    setErrors({});

    // Trigger validation of all fields
    const validations = Object.keys(newUser).map((key) => validate(key));

    // If any value in errors has some value - we abort
    if (validations.includes(false)) {
      return;
    }

    try {
      const response = await APIclient.auth.registrations.create({
        ...newUser,
        configToken: token,
      });
      if ([USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN].includes(response.role)) {
        navigate("/admin");
      } else {
        navigate("/teacher");
      }
    } catch (e) {
      // Check whether the translation is present and return the fallback message if not
      if (typeof e.error === "string") {
        const translation = translate(e.error);
        addError({ global: translation });
        if (translation) return;
      }
      const fallbackMsg = translate("form_validation_register_error");
      addError({ global: fallbackMsg });
    }
  };

  const validate = (field) => {
    if (field === "email" && token) {
      return true;
    }

    if (newUser[field] === "") {
      addError({ [field]: translate("form_validation_required") });
      return false;
    } else if (field === "confirmPassword" || field === "password") {
      const passwordMatch = newUser.password === newUser.confirmPassword;
      if (newUser[field].length < 4) {
        addError({ [field]: translate("form_validation_password_min_length") });
        return false;
      } else {
        const msg = passwordMatch
          ? ""
          : translate("form_validation_password_match");
        addError({ password: msg, confirmPassword: msg });
        return passwordMatch;
      }
    } else if (field === "email") {
      const match = newUser[field].trim().match(EMAIL_REGEXP) !== null;
      const msg = match ? "" : "form_validation_email";
      addError({ [field]: msg });
      return match;
    } else {
      addError({ [field]: "" });
      return true;
    }
  };

  const getErrorValidation = (field) => {
    return {
      error: errors[field] && errors[field].length > 0,
      helperText: errors[field],
      onBlur: () => validate(field),
    };
  };

  return (
    <form className="container container--max-xs" onSubmit={register}>
      <h1>{translate("teacher_register_title")}</h1>
      {errors.global && <p className="error-message">{errors.global}</p>}
      <div style={{ display: "flex", flexDirection: "column" }}>
        <TextField
          label={translate("teacher_register_first_name")}
          className="normal-input"
          {...getErrorValidation("firstName")}
          value={newUser.firstName}
          onChange={(e) =>
            setNewUser({ ...newUser, firstName: e.target.value })
          }
        />
        <TextField
          label={translate("teacher_register_last_name")}
          className="normal-input"
          {...getErrorValidation("lastName")}
          value={newUser.lastName}
          onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
        />
        {!token && (
          <TextField
            label={translate("teacher_register_email")}
            className="normal-input"
            type="email"
            {...getErrorValidation("email")}
            value={newUser.email}
            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
          />
        )}
        <TextField
          label={translate("teacher_register_password")}
          className="normal-input"
          {...getErrorValidation("password")}
          type="password"
          value={newUser.password}
          onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
        />
        <TextField
          label={translate("teacher_register_password_confirm")}
          className="normal-input"
          {...getErrorValidation("confirmPassword")}
          type="password"
          value={newUser.confirmPassword}
          onChange={(e) =>
            setNewUser({ ...newUser, confirmPassword: e.target.value })
          }
        />
        <TextField
          label={translate("teacher_register_organisation")}
          className="normal-input"
          {...getErrorValidation("organisation")}
          value={newUser.organisation}
          onChange={(e) =>
            setNewUser({ ...newUser, organisation: e.target.value })
          }
        />
        <TextField
          label={translate("teacher_register_location")}
          className="normal-input"
          {...getErrorValidation("organisationLocation")}
          value={newUser.organisationLocation}
          onChange={(e) =>
            setNewUser({ ...newUser, organisationLocation: e.target.value })
          }
        />
        <div className="audienceContainer">
          <label>{translate("teacher_register_target_audience")}</label>
          <Select
            {...getErrorValidation("targetAudience")}
            value={newUser.targetAudience}
            onChange={(e) =>
              setNewUser({ ...newUser, targetAudience: e.target.value })
            }
          >
            {Object.values(TARGET_AUDIENCES).map((audience) => (
              <MenuItem value={audience} key={audience}>
                {audience}
              </MenuItem>
            ))}
          </Select>
          {errors["targetAudience"] && errors["targetAudience"].length > 0 && (
            <span className="MuiFormHelperText-root MuiFormHelperText-contained Mui-error">
              {errors["targetAudience"]}
            </span>
          )}
        </div>
      </div>
      <button className="button" type="submit">
        {translate("teacher_register_title")}
      </button>
    </form>
  );
}
